/**
 * Analytics helper for consistent event logging
 */

import { logEvent, Analytics } from 'firebase/analytics';
import { analytics } from './firebase';

// Event names
export const ANALYTICS_EVENTS = {
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  CHECKIN_OPENED: 'checkin_opened',
  CHECKIN_SUBMITTED: 'checkin_submitted',
  PLAN_VIEWED: 'plan_viewed',
  ACTION_OPENED: 'action_opened',
  ACTION_COMPLETED: 'action_completed',
  CREDITS_AWARDED: 'credits_awarded',
  MOMENTUM_VIEWED: 'momentum_viewed',
  // Partner events
  PARTNER_INVITE_CREATED: 'partner_invite_created',
  PARTNER_INVITE_ACCEPTED: 'partner_invite_accepted',
  PARTNER_NUDGE_SENT: 'partner_nudge_sent',
  PARTNER_NUDGE_VIEWED: 'partner_nudge_viewed',
  PARTNER_SHARING_CHANGED: 'partner_sharing_changed',
  // De-escalation events
  DEESCALATION_ENTERED: 'deescalation_entered',
  DEESCALATION_EXITED: 'deescalation_exited',
  // Notification events
  NOTIFICATION_PREFS_CHANGED: 'notification_prefs_changed',
  // Overwhelmed / De-escalation flow
  OVERWHELMED_OPENED: 'overwhelmed_opened',
  OVERWHELMED_CHOICE_SELECTED: 'overwhelmed_choice_selected',
  OVERWHELMED_BREATH_STARTED: 'overwhelmed_breath_started',
  OVERWHELMED_BREATH_COMPLETED: 'overwhelmed_breath_completed',
  OVERWHELMED_COACH_OPENED: 'overwhelmed_coach_opened',
  // Coach events
  COACH_OPENED: 'coach_opened',
  COACH_INTENT_SELECTED: 'coach_intent_selected',
  COACH_MESSAGE_SENT: 'coach_message_sent',
  COACH_RESPONSE_RECEIVED: 'coach_response_received',
  COACH_FALLBACK_USED: 'coach_fallback_used',
  COACH_ACTION_SELECTED: 'coach_action_selected',
  COACH_ERROR: 'coach_error',
  // Enhanced chat events
  CHAT_OPEN: 'chat_open',
  CHAT_SEND: 'chat_send',
  CHAT_ACTION_CLICK: 'chat_action_click',
  CHAT_CHIP_CLICK: 'chat_chip_click',
  CHAT_CLOSE: 'chat_close',
  // Navigation events
  NAV_OPEN_SIDEBAR: 'nav_open_sidebar',
  NAV_BRAND_TAP: 'nav_brand_tap',
  // Breathing exercise events
  BREATH_OPEN: 'breath_open',
  BREATH_START: 'breath_start',
  BREATH_PAUSE: 'breath_pause',
  BREATH_RESUME: 'breath_resume',
  BREATH_PATTERN_CHANGED: 'breath_pattern_changed',
  BREATH_COMPLETE: 'breath_complete',
  BREATH_FEEDBACK: 'breath_feedback',
  BREATH_CLOSED: 'breath_closed',
  // Reminder events
  REMINDER_BANNER_DISPLAYED: 'reminder_banner_displayed',
  REMINDER_BANNER_CLICKED: 'reminder_banner_clicked',
  REMINDER_BANNER_DISMISSED: 'reminder_banner_dismissed',
  // PWA / Service Worker events
  SW_REGISTERED: 'sw_registered',
  UPDATE_FOUND: 'update_found',
  UPDATE_APPLIED: 'update_applied',
  UPDATE_DISMISSED: 'update_dismissed',
  UPDATE_REQUIRED_SHOWN: 'update_required_shown',
  PWA_INSTALLED: 'pwa_installed',
  // Doctor Q&A events
  DOCTORQA_OPENED: 'doctorqa_opened',
  DOCTORQA_QUESTION_CREATED: 'doctorqa_question_created',
  DOCTORQA_QUESTION_DELETED: 'doctorqa_question_deleted',
  DOCTORQA_QUESTION_CLOSED: 'doctorqa_question_closed',
  DOCTORQA_ANSWER_ADDED: 'doctorqa_answer_added',
  DOCTORQA_QUESTION_REOPENED: 'doctorqa_question_reopened',
  DOCTORQA_OPENED_FROM_CHAT: 'doctorqa_opened_from_chat',
  // Visit Brief events
  VISIT_BRIEF_OPENED: 'visit_brief_opened',
  VISIT_BRIEF_GENERATED: 'visit_brief_generated',
  VISIT_BRIEF_SHARED: 'visit_brief_shared',
  VISIT_BRIEF_COPIED: 'visit_brief_copied',
  VISIT_BRIEF_ERROR: 'visit_brief_error',
  // Enhanced Doctor Q&A events
  DOCTORQA_QUICK_CAPTURE_OPENED: 'doctorqa_quick_capture_opened',
  DOCTORQA_QUICK_CAPTURE_SAVED: 'doctorqa_quick_capture_saved',
  DOCTORQA_ACTION_ITEM_TOGGLED: 'doctorqa_action_item_toggled',
  DOCTORQA_APPOINTMENT_SET: 'doctorqa_appointment_set',
  DOCTORQA_HISTORY_VIEWED: 'doctorqa_history_viewed',
  // Onboarding interaction events (for iOS regression tracking)
  ONBOARDING_BIRTHDAY_FOCUSED: 'onboarding_birthday_focused',
  ONBOARDING_BIRTHDAY_CHANGED: 'onboarding_birthday_changed',
  ONBOARDING_CONTINUE_PRESSED: 'onboarding_continue_pressed',
  ONBOARDING_VALIDATION_FAILED: 'onboarding_validation_failed',
} as const;

type EventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

/**
 * Log analytics event with standardized format
 * Safe to call even if analytics isn't available
 */
export async function log(
  eventName: EventName,
  params?: Record<string, string | number | boolean>
): Promise<void> {
  try {
    const analyticsInstance = await analytics;
    if (analyticsInstance) {
      logEvent(analyticsInstance as Analytics, eventName, params);
    }
    
    // Also log to console in development
    if (import.meta.env.DEV) {
      console.log(`📊 [Analytics] ${eventName}`, params || '');
    }
  } catch (error) {
    // Silently fail - analytics should never break the app
    if (import.meta.env.DEV) {
      console.warn('Analytics error:', error);
    }
  }
}

// Convenience methods for specific events
export const trackCheckinOpened = () => log(ANALYTICS_EVENTS.CHECKIN_OPENED);

export const trackCheckinSubmitted = (params: {
  mood: number;
  energy?: number;
  stress?: number;
  has_note: boolean;
  state: string;
}) => log(ANALYTICS_EVENTS.CHECKIN_SUBMITTED, params);

export const trackActionViewed = (params: {
  action_id: string;
  category: string;
}) => log(ANALYTICS_EVENTS.ACTION_OPENED, params);

export const trackActionCompleted = (params: {
  action_id: string;
  category: string;
}) => log(ANALYTICS_EVENTS.ACTION_COMPLETED, params);

export const trackOnboardingStarted = () => log(ANALYTICS_EVENTS.ONBOARDING_STARTED);

export const trackOnboardingCompleted = () => log(ANALYTICS_EVENTS.ONBOARDING_COMPLETED);

// Generic event tracking for coach and other features
export const trackEvent = (
  eventName: string,
  params?: Record<string, string | number | boolean>
) => log(eventName as EventName, params);

// Alias for more descriptive naming
export const logAnalyticsEvent = trackEvent;
